import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { startTryOn, getTryOnStatus, getTryOnQuota, TryOnQuotaResponse } from '../services/api';
import '../styles/lensTryOn.css';

type Stage = 'capturing' | 'previewing' | 'generating' | 'finishing' | 'ready' | 'failed';

interface OutfitOption {
  id: string;
  label: string;
  icon: string;
}

interface QualityOption {
  id: string;
  label: string;
  icon: string;
  tagline: string;
}

const QUALITY_OPTIONS: QualityOption[] = [
  { id: 'fast', label: 'Rápida', icon: 'bolt', tagline: 'Prueba varios estilos rápido' },
  { id: 'pro', label: 'Alta calidad', icon: 'hd', tagline: 'Ideal para la foto final' },
];

const DEFAULT_QUALITY = 'fast';
const QUALITY_STORAGE_KEY = 'lens_tryon_quality';

const OUTFIT_OPTIONS: OutfitOption[] = [
  { id: 'gala', label: 'Formal de gala', icon: 'diamond' },
  { id: 'oficina', label: 'Oficina', icon: 'work' },
  { id: 'deportivo', label: 'Deportivo', icon: 'directions_run' },
  { id: 'frio', label: 'Frío', icon: 'ac_unit' },
];

const STATUS_MESSAGES = [
  'Analizando el rostro...',
  'Detectando el marco...',
  'Ajustando el estilo elegido...',
  'Aplicando iluminación natural...',
  'Finalizando vista previa...',
];

const POLL_INTERVAL_MS = 2000;
const STATUS_CYCLE_MS = 1400;

// 3x3 pixel-grid loader: a chevron-shaped wavefront of per-cell animation
// delays, so the lit cells appear to sweep across the grid.
const CHEVRON_DELAYS = Array.from({ length: 9 }, (_, i) => {
  const r = Math.floor(i / 3);
  const c = i % 3;
  return (c + Math.abs(r - 1)) * 90;
});

// Duration lives only in the .lens-pixel CSS class (lensTryOn.css) — each
// cell only needs its own delay, not a per-cell copy of the shared duration.
const PixelLoader = () => (
  <span aria-hidden className="grid shrink-0 grid-cols-[repeat(3,6px)] gap-[2px]">
    {CHEVRON_DELAYS.map((delay, index) => (
      <span
        key={index}
        className="lens-pixel block h-[6px] w-[6px] rounded-[1px] bg-primary"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </span>
);

// Real generation time isn't known upfront and can run well past a minute,
// so tenths-of-a-second precision (and the 10 renders/sec it costs) buys
// nothing — whole seconds is all this is meant to convey.
const useElapsed = () => {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}m ${String(rest).padStart(2, '0')}s`;
};

const GeneratingStatus = ({ label }: { label: string }) => {
  const elapsed = useElapsed();
  return (
    <div className="mt-2 flex items-center gap-2.5">
      <PixelLoader />
      {/* per-second elapsed count is hidden from assistive tech so it doesn't
          get re-announced every second. */}
      <span aria-hidden className="font-mono text-xs tabular-nums text-gray-400">
        {elapsed}
      </span>
      {/* role="status" announces the label on each change. */}
      <span role="status" className="lens-shimmer-text text-[13px] font-medium">
        {label}
      </span>
    </div>
  );
};

const CornerBrackets = ({ color = '#2563eb' }: { color?: string }) => (
  <>
    <div
      className="pointer-events-none absolute left-4 top-4 h-7 w-7 rounded-tl-md"
      style={{ borderTop: `2.5px solid ${color}`, borderLeft: `2.5px solid ${color}` }}
    />
    <div
      className="pointer-events-none absolute right-4 top-4 h-7 w-7 rounded-tr-md"
      style={{ borderTop: `2.5px solid ${color}`, borderRight: `2.5px solid ${color}` }}
    />
    <div
      className="pointer-events-none absolute bottom-4 left-4 h-7 w-7 rounded-bl-md"
      style={{ borderBottom: `2.5px solid ${color}`, borderLeft: `2.5px solid ${color}` }}
    />
    <div
      className="pointer-events-none absolute bottom-4 right-4 h-7 w-7 rounded-br-md"
      style={{ borderBottom: `2.5px solid ${color}`, borderRight: `2.5px solid ${color}` }}
    />
  </>
);

const DiffusionOrbs = () => (
  <div className="pointer-events-none absolute -inset-[6%]">
    <div
      className="lens-orb-1 absolute left-[4%] top-[6%] h-[280px] w-[280px]"
      style={{
        mixBlendMode: 'multiply',
        filter: 'blur(46px) saturate(1.7)',
        background: 'radial-gradient(circle at 35% 30%, #93c5fd 0%, #2563eb 55%, transparent 82%)',
        opacity: 0.75,
      }}
    />
    <div
      className="lens-orb-2 absolute bottom-[4%] right-0 h-[250px] w-[250px]"
      style={{
        mixBlendMode: 'multiply',
        filter: 'blur(50px) saturate(1.7)',
        background: 'radial-gradient(circle at 32% 28%, #60a5fa 0%, #1d4ed8 55%, transparent 82%)',
        opacity: 0.72,
      }}
    />
    <div
      className="lens-orb-3 absolute bottom-[10%] left-[6%] h-[210px] w-[210px]"
      style={{
        mixBlendMode: 'multiply',
        filter: 'blur(40px) saturate(1.8)',
        background: 'radial-gradient(circle at 35% 30%, #bfdbfe 0%, #2563eb 55%, transparent 82%)',
        opacity: 0.65,
      }}
    />
    <div
      className="lens-orb-4 absolute right-[6%] top-[4%] h-[190px] w-[190px]"
      style={{
        mixBlendMode: 'multiply',
        filter: 'blur(38px) saturate(1.8)',
        background: 'radial-gradient(circle at 30% 25%, #38bdf8 0%, #1e40af 55%, transparent 82%)',
        opacity: 0.6,
      }}
    />
  </div>
);

const ConvergingOrbs = () => (
  <div className="pointer-events-none absolute -inset-[6%]">
    <div
      className="lens-orb-converge-1 absolute left-[4%] top-[6%] h-[280px] w-[280px] rounded-full"
      style={{
        mixBlendMode: 'multiply',
        filter: 'blur(46px) saturate(1.7)',
        background: 'radial-gradient(circle at 35% 30%, #93c5fd 0%, #2563eb 55%, transparent 82%)',
        opacity: 0.75,
      }}
    />
    <div
      className="lens-orb-converge-2 absolute bottom-[4%] right-0 h-[250px] w-[250px] rounded-full"
      style={{
        mixBlendMode: 'multiply',
        filter: 'blur(50px) saturate(1.7)',
        background: 'radial-gradient(circle at 32% 28%, #60a5fa 0%, #1d4ed8 55%, transparent 82%)',
        opacity: 0.72,
      }}
    />
    <div
      className="lens-orb-converge-3 absolute bottom-[10%] left-[6%] h-[210px] w-[210px] rounded-full"
      style={{
        mixBlendMode: 'multiply',
        filter: 'blur(40px) saturate(1.8)',
        background: 'radial-gradient(circle at 35% 30%, #bfdbfe 0%, #2563eb 55%, transparent 82%)',
        opacity: 0.65,
      }}
    />
    <div
      className="lens-orb-converge-4 absolute right-[6%] top-[4%] h-[190px] w-[190px] rounded-full"
      style={{
        mixBlendMode: 'multiply',
        filter: 'blur(38px) saturate(1.8)',
        background: 'radial-gradient(circle at 30% 25%, #38bdf8 0%, #1e40af 55%, transparent 82%)',
        opacity: 0.6,
      }}
    />
  </div>
);

export const LensTryOn = () => {
  const { visit_id } = useParams<{ visit_id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const frameDescription: string = (location.state as { frameDescription?: string } | null)?.frameDescription || '';

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const statusIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const finishTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const readyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [stage, setStage] = useState<Stage>('capturing');
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const [flashOn, setFlashOn] = useState(true);
  const [flashActive, setFlashActive] = useState(false);
  const [selectedOutfit, setSelectedOutfit] = useState<string | null>(null);
  // Remembers the user's last choice across sessions (device-local); defaults to fast.
  const [quality, setQuality] = useState<string>(() => {
    try {
      return localStorage.getItem(QUALITY_STORAGE_KEY) || DEFAULT_QUALITY;
    } catch {
      return DEFAULT_QUALITY;
    }
  });
  const [tryOnQuota, setTryOnQuota] = useState<TryOnQuotaResponse | null>(null);
  const [statusIndex, setStatusIndex] = useState(0);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const goBackToHandoff = () => {
    if (visit_id) {
      navigate(`/commercial-handoff/${visit_id}`);
    } else {
      navigate('/home');
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const clearTimers = () => {
    if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
    if (finishTimeoutRef.current) clearTimeout(finishTimeoutRef.current);
    if (readyTimeoutRef.current) clearTimeout(readyTimeoutRef.current);
    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    pollTimeoutRef.current = null;
    statusIntervalRef.current = null;
    finishTimeoutRef.current = null;
    readyTimeoutRef.current = null;
    flashTimeoutRef.current = null;
  };

  const startCamera = async (isCancelled: () => boolean) => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });
      if (isCancelled()) {
        // Screen was closed/unmounted while getUserMedia was pending — the
        // cleanup below already ran and found nothing to stop, so release
        // this stream immediately instead of leaving the camera on.
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch {
      if (!isCancelled()) {
        setCameraError('No se pudo acceder a la cámara. Verifique los permisos e intente nuevamente.');
      }
    }
  };

  useEffect(() => {
    let cancelled = false;
    if (stage === 'capturing') {
      startCamera(() => cancelled);
    }
    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [stage]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  useEffect(() => {
    getTryOnQuota()
      .then(setTryOnQuota)
      .catch(() => setTryOnQuota(null));
  }, []);

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setPhoto(dataUrl);
    stopCamera();
    setStage('previewing');
  };

  const handleCapture = () => {
    if (!flashOn) {
      captureFrame();
      return;
    }
    // Turn the whole screen white first so it acts as a light source for the
    // front camera, hold it through the shot, then fade back out.
    setFlashActive(true);
    flashTimeoutRef.current = setTimeout(() => {
      captureFrame();
      flashTimeoutRef.current = setTimeout(() => {
        setFlashActive(false);
      }, 120);
    }, 180);
  };

  const handleRetake = () => {
    setPhoto(null);
    setSelectedOutfit(null);
    setStage('capturing');
  };

  const pollJob = (jobId: string) => {
    const poll = async () => {
      try {
        const status = await getTryOnStatus(jobId);
        if (status.status === 'completed') {
          if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
          statusIntervalRef.current = null;
          const raw = status.result || '';
          const dataUrl = raw.startsWith('data:') ? raw : `data:image/jpeg;base64,${raw}`;
          finishTimeoutRef.current = setTimeout(() => {
            setStage('finishing');
            readyTimeoutRef.current = setTimeout(() => {
              setResult(dataUrl);
              setStage('ready');
            }, 1000);
          }, 250);
          return;
        }
        if (status.status === 'failed') {
          if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
          statusIntervalRef.current = null;
          setError(status.error || 'No se pudieron generar las imágenes.');
          setStage('failed');
          return;
        }
        pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
        setError(err instanceof Error ? err.message : 'Ocurrió un error al consultar la generación');
        setStage('failed');
      }
    };
    poll();
  };

  const handleSelectOutfit = (outfitId: string) => {
    setSelectedOutfit(outfitId);
  };

  const handleSelectQuality = (qualityId: string) => {
    setQuality(qualityId);
    try {
      localStorage.setItem(QUALITY_STORAGE_KEY, qualityId);
    } catch {
      // Best-effort — private browsing / storage disabled just means no memory next time.
    }
  };

  // Only shown once the clinic has no generations left this month — otherwise
  // the screen looks exactly as it does today.
  const quotaExceededMessage = (() => {
    if (!tryOnQuota) return null;
    if (!tryOnQuota.configured) return 'Generación no habilitada para esta clínica';
    if (tryOnQuota.used >= tryOnQuota.limit) {
      return `Límite mensual alcanzado (${tryOnQuota.used} de ${tryOnQuota.limit})`;
    }
    return null;
  })();

  const handleGenerateLook = async () => {
    if (!visit_id || !photo || !selectedOutfit || quotaExceededMessage) return;
    setError(null);
    setStage('generating');
    setStatusIndex(0);

    statusIntervalRef.current = setInterval(() => {
      setStatusIndex((i) => (i + 1) % STATUS_MESSAGES.length);
    }, STATUS_CYCLE_MS);

    try {
      const response = await startTryOn(visit_id, photo, frameDescription, selectedOutfit, quality);
      if (response.success && response.job_id) {
        pollJob(response.job_id);
      } else {
        if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
        setError(response.error || 'No se pudo iniciar la generación de imágenes');
        setStage('failed');
      }
    } catch (err) {
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
      setError(err instanceof Error ? err.message : 'Ocurrió un error al iniciar la generación');
      setStage('failed');
    }
  };

  const handleTryOthers = () => {
    clearTimers();
    setResult(null);
    setError(null);
    setSelectedOutfit(null);
    setStage('previewing');
  };

  const selectedOutfitLabel = OUTFIT_OPTIONS.find((o) => o.id === selectedOutfit)?.label || '';

  return (
    <div className="relative flex min-h-screen flex-col bg-[#f6f7f8]">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-[100] bg-white transition-opacity duration-150 ease-out"
        style={{ opacity: flashActive ? 1 : 0 }}
      />
      <button
        onClick={goBackToHandoff}
        className="absolute right-6 top-6 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#101822] shadow-[0_4px_10px_-2px_rgba(0,0,0,0.12)] transition-transform duration-200 hover:scale-105 hover:bg-gray-50 active:scale-95"
        title="Cerrar"
      >
        <span className="material-symbols-outlined text-2xl">close</span>
      </button>

      <div className="flex flex-1 flex-col items-center justify-center gap-10 p-6 md:flex-row md:p-14">
        {/* Photo / animation frame */}
        <div className="flex w-full max-w-[420px] items-center justify-center md:w-[420px] md:shrink-0">
          {stage === 'capturing' && (
            <div className="relative aspect-[3/4] w-full overflow-hidden rounded-3xl border border-gray-200 bg-[#fafbfc]">
              <video ref={videoRef} autoPlay playsInline muted className="h-full w-full object-cover" />
              <canvas ref={canvasRef} className="hidden" />
              <button
                onClick={() => setFlashOn((f) => !f)}
                className={`absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-full shadow-[0_4px_10px_-2px_rgba(0,0,0,0.15)] transition-all duration-200 hover:scale-105 active:scale-95 ${
                  flashOn ? 'bg-amber-400 text-white' : 'bg-white/90 text-[#101822]'
                }`}
                title={flashOn ? 'Desactivar flash' : 'Activar flash'}
              >
                <span
                  className="material-symbols-outlined text-xl"
                  style={{ fontVariationSettings: `"FILL" ${flashOn ? 1 : 0}` }}
                >
                  bolt
                </span>
              </button>
              {cameraError && (
                <div className="absolute inset-x-3 bottom-3 rounded-xl border border-red-200 bg-red-50 p-3 text-center">
                  <p className="text-xs font-medium text-red-600">{cameraError}</p>
                  <button
                    onClick={() => startCamera(() => false)}
                    className="mt-2 rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-bold text-red-600 transition-colors duration-200 hover:bg-red-100"
                  >
                    Reintentar
                  </button>
                </div>
              )}
            </div>
          )}

          {stage === 'previewing' && photo && (
            <div className="relative aspect-[3/4] w-full overflow-hidden rounded-3xl border border-gray-200 bg-[#fafbfc]">
              <img src={photo} alt="Foto capturada" className="h-full w-full object-cover" />
              <CornerBrackets color="rgba(37,99,235,0.35)" />
              <button
                onClick={handleRetake}
                className="absolute bottom-[18px] left-1/2 flex h-12 -translate-x-1/2 items-center gap-2 rounded-full bg-[#dc2626] px-[22px] shadow-[0_10px_15px_-3px_rgba(0,0,0,0.15),0_4px_6px_-4px_rgba(0,0,0,0.15)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#b91c1c] hover:shadow-xl"
              >
                <span className="material-symbols-outlined text-xl text-white">close</span>
                <span className="text-sm font-bold text-white">Cambiar imagen</span>
              </button>
            </div>
          )}

          {stage === 'generating' && (
            <div className="relative aspect-[3/4] w-full overflow-hidden rounded-3xl border border-gray-200 bg-[#fafbfc] [isolation:isolate]">
              <DiffusionOrbs />
              <div
                className="lens-grain absolute inset-0"
                style={{ mixBlendMode: 'overlay', opacity: 0.5 }}
              />
              <CornerBrackets />
            </div>
          )}

          {stage === 'finishing' && (
            <div className="relative aspect-[3/4] w-full overflow-hidden rounded-3xl border border-gray-200 bg-[#fafbfc] [isolation:isolate]">
              <ConvergingOrbs />
              <div
                className="lens-iris-in absolute inset-0"
                style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', clipPath: 'circle(0% at 50% 50%)' }}
              />
              <div
                className="lens-grain absolute inset-0"
                style={{ mixBlendMode: 'overlay', opacity: 0.5 }}
              />
            </div>
          )}

          {stage === 'ready' && (
            <div
              className="relative aspect-[3/4] w-full overflow-hidden rounded-3xl border border-[rgba(37,99,235,0.25)] bg-[#fafbfc]"
              style={{ boxShadow: '0 0 0 1px rgba(37,99,235,0.08), 0 24px 40px -20px rgba(37,99,235,0.35)' }}
            >
              {result ? (
                <img src={result} alt="Look generado" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-sm text-gray-400">Sin imagen</div>
              )}
              <div className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-[#2563eb] px-3 py-1.5">
                <span className="material-symbols-outlined text-[13px] text-white">auto_awesome</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-white">Generado con IA</span>
              </div>
              <div
                className="lens-iris-out absolute inset-0"
                style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', clipPath: 'circle(150% at 50% 50%)' }}
              />
            </div>
          )}

          {stage === 'failed' && (
            <div className="flex aspect-[3/4] w-full flex-col items-center justify-center gap-4 rounded-3xl border border-red-200 bg-red-50 p-8 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <span className="material-symbols-outlined text-3xl text-red-600">error_outline</span>
              </div>
              <div>
                <p className="mb-1.5 text-base font-bold text-red-800">No se pudo generar la imagen</p>
                <p className="text-sm leading-relaxed text-red-700">
                  {error || 'Ocurrió un problema al procesar tu foto. Intenta de nuevo.'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Control panel */}
        <div className="flex w-full max-w-md flex-col justify-center gap-6">
          {stage === 'capturing' && (
            <>
              <div className="flex flex-col gap-3">
                <h2 className="text-2xl font-extrabold text-[#101822] md:text-[26px]">
                  ¿Cómo te verás con tus nuevos lentes?
                </h2>
                <p className="max-w-[400px] text-[15px] leading-relaxed text-gray-500">
                  Centra tu rostro dentro del marco y mantén una expresión natural. La IA usará esta foto para
                  generar tu nuevo look.
                </p>
              </div>
              <button
                onClick={handleCapture}
                className="mt-2 flex w-full max-w-[320px] items-center justify-center gap-2 rounded-3xl bg-primary px-8 py-4 text-[17px] font-bold text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1d4ed8] hover:shadow-xl active:translate-y-0"
              >
                <span className="material-symbols-outlined text-xl">photo_camera</span>
                <span>Capturar foto</span>
              </button>
            </>
          )}

          {stage === 'previewing' && (
            <>
              <div className="flex flex-col gap-3">
                <h2 className="text-2xl font-extrabold text-[#101822] md:text-[26px]">Confirma tu foto</h2>
                <p className="max-w-[400px] text-[15px] leading-relaxed text-gray-500">
                  ¿Se ve bien? Elige un estilo para generar tu look, o cambia la imagen.
                </p>
              </div>
              <div className="flex max-w-[400px] flex-col gap-3.5">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Elige un estilo</span>
                <div className="grid grid-cols-2 gap-2.5">
                  {OUTFIT_OPTIONS.map((opt) => {
                    const isSelected = selectedOutfit === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => handleSelectOutfit(opt.id)}
                        className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border-2 px-2.5 py-3.5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                          isSelected ? '' : 'border-gray-200 bg-white hover:border-primary/50 hover:bg-blue-50/50'
                        }`}
                        style={
                          isSelected
                            ? { borderColor: '#2563eb', background: 'rgba(37,99,235,0.06)' }
                            : undefined
                        }
                      >
                        <span
                          className="material-symbols-outlined text-2xl"
                          style={{ color: isSelected ? '#2563eb' : '#101822' }}
                        >
                          {opt.icon}
                        </span>
                        <span
                          className="text-center text-[13px] font-semibold"
                          style={{ color: isSelected ? '#2563eb' : '#101822' }}
                        >
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Calidad de generación
                </span>
                <div className="grid grid-cols-2 gap-2.5">
                  {QUALITY_OPTIONS.map((opt) => {
                    const isSelected = quality === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => handleSelectQuality(opt.id)}
                        className={`flex flex-col items-center gap-1 rounded-2xl border-2 p-2.5 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                          isSelected ? '' : 'border-gray-200 bg-white hover:border-primary/50 hover:bg-blue-50/50'
                        }`}
                        style={
                          isSelected
                            ? { borderColor: '#2563eb', background: 'rgba(37,99,235,0.06)' }
                            : undefined
                        }
                      >
                        <div className="flex items-center gap-1.5">
                          <span
                            className="material-symbols-outlined text-xl"
                            style={{ color: isSelected ? '#2563eb' : '#101822' }}
                          >
                            {opt.icon}
                          </span>
                          <span
                            className="text-[13px] font-semibold"
                            style={{ color: isSelected ? '#2563eb' : '#101822' }}
                          >
                            {opt.label}
                          </span>
                        </div>
                        <span className="text-center text-[11px] leading-tight text-gray-500">{opt.tagline}</span>
                      </button>
                    );
                  })}
                </div>

                {quotaExceededMessage && (
                  <div className="flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-[15px] text-red-600">error</span>
                    <span className="text-[12.5px] font-semibold text-red-600">{quotaExceededMessage}</span>
                  </div>
                )}

                <button
                  onClick={handleGenerateLook}
                  disabled={!selectedOutfit || !!quotaExceededMessage}
                  className={`mt-0.5 flex w-full items-center justify-center gap-2 rounded-3xl bg-primary px-8 py-4 text-base font-bold text-white shadow-lg transition-all duration-200 disabled:cursor-not-allowed ${
                    selectedOutfit && !quotaExceededMessage ? 'hover:-translate-y-0.5 hover:bg-[#1d4ed8] hover:shadow-xl' : ''
                  }`}
                  style={{ opacity: selectedOutfit && !quotaExceededMessage ? 1 : 0.4 }}
                >
                  <span>Generar look</span>
                </button>
              </div>
            </>
          )}

          {stage === 'generating' && (
            <div className="flex flex-col gap-4">
              <h2 className="text-2xl font-extrabold text-[#101822] md:text-[26px]">Generando tu look</h2>
              <GeneratingStatus label={STATUS_MESSAGES[statusIndex]} />
            </div>
          )}

          {stage === 'finishing' && (
            <div className="flex flex-col gap-4">
              <h2 className="text-2xl font-extrabold text-[#101822] md:text-[26px]">
                Aplicando los toques finales
              </h2>
              <p className="max-w-[400px] text-[15px] leading-relaxed text-gray-500">
                Ya casi está. Tu look <span className="font-semibold text-[#101822]">{selectedOutfitLabel}</span>{' '}
                está listo.
              </p>
            </div>
          )}

          {stage === 'ready' && (
            <>
              <div className="flex flex-col gap-3">
                <h2 className="text-2xl font-extrabold text-[#101822] md:text-[26px]">Tu nuevo look</h2>
                <p className="max-w-[400px] text-[15px] leading-relaxed text-gray-500">
                  Look <span className="font-semibold text-[#101822]">{selectedOutfitLabel}</span> generado. Tu
                  marco, fondo e iluminación fueron ajustados automáticamente.
                </p>
              </div>
              <div className="mt-2 flex max-w-[400px] gap-3">
                <button
                  onClick={handleTryOthers}
                  className="flex flex-1 items-center justify-center gap-2 rounded-3xl border-2 border-primary bg-white px-4 py-4 text-[15px] font-bold text-primary shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary hover:text-white hover:shadow-xl"
                >
                  Probar otro estilo
                </button>
                <button
                  onClick={goBackToHandoff}
                  className="flex flex-[1.3] items-center justify-center gap-2 rounded-3xl bg-primary px-4 py-4 text-[15px] font-bold text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1d4ed8] hover:shadow-xl"
                >
                  Elijo estos lentes
                </button>
              </div>
              <button
                onClick={handleRetake}
                className="mt-3 flex max-w-[400px] items-center justify-center gap-2 rounded-3xl border-2 border-red-500 bg-white px-4 py-4 text-[15px] font-bold text-red-600 shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-red-50 hover:shadow-xl"
              >
                Tomar otra foto
              </button>
            </>
          )}

          {stage === 'failed' && (
            <div className="flex max-w-[400px] gap-3">
              <button
                onClick={goBackToHandoff}
                className="flex flex-1 items-center justify-center gap-2 rounded-3xl border-2 border-primary bg-white px-4 py-4 text-[15px] font-bold text-primary shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-primary hover:text-white hover:shadow-xl"
              >
                Regresar
              </button>
              <button
                onClick={handleTryOthers}
                className="flex flex-1 items-center justify-center gap-2 rounded-3xl bg-primary px-4 py-4 text-[15px] font-bold text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1d4ed8] hover:shadow-xl"
              >
                <span className="material-symbols-outlined text-lg">refresh</span>
                Intentar de nuevo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
